import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function AdPaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("processing");
  const [error, setError] = useState("");
  const hasCaptured = useRef(false);
  const orderId = searchParams.get("token") || "";

  useEffect(() => {
    if (hasCaptured.current) return;
    hasCaptured.current = true;
    if (!orderId) {
      setStatus("error");
      setError("Référence de paiement manquante.");
      return;
    }
    const capture = async () => {
      try {
        const res = await api.post(`/ads/campaigns/capture/${orderId}`);
        setStatus(res.data.payment_status === "paid" ? "paid" : "pending");
      } catch (err) {
        setStatus("error");
        setError(formatApiError(err));
      }
    };
    capture();
  }, [orderId]);

  return (
    <div data-testid="ad-payment-success-page" className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-10 text-center">
        <div className="flex justify-center mb-6"><Logo /></div>
        {status === "processing" && (
          <>
            <Loader2 className="w-10 h-10 text-brand-turquoise animate-spin mx-auto mb-4" />
            <p className="text-brand-navy font-medium">Activation de votre campagne…</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-brand-turquoise mx-auto mb-4" />
            <h1 data-testid="ad-payment-success-title" className="text-xl font-bold text-brand-navy mb-2">Campagne activée</h1>
            <p className="text-sm text-slate-500 mb-6">Votre paiement a été reçu. Votre annonce est maintenant diffusée sur NEXA.</p>
          </>
        )}
        {status === "pending" && (
          <>
            <Loader2 className="w-10 h-10 text-brand-orange animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold text-brand-navy mb-2">Paiement en cours de traitement</h1>
            <p className="text-sm text-slate-500 mb-6">PayPal finalise la transaction. Votre campagne s'activera sous peu.</p>
          </>
        )}
        {status === "error" && (
          <>
            <h1 data-testid="ad-payment-error-title" className="text-xl font-bold text-red-600 mb-2">Erreur</h1>
            <p data-testid="ad-payment-error-message" className="text-sm text-slate-500 mb-6">{error}</p>
          </>
        )}
        <button
          data-testid="ad-payment-success-back"
          onClick={() => navigate("/publicite")}
          className="w-full bg-brand-navy text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-blue transition-colors"
        >
          Voir mes campagnes
        </button>
      </div>
    </div>
  );
}

export function AdPaymentCancel() {
  const navigate = useNavigate();
  return (
    <div data-testid="ad-payment-cancel-page" className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-10 text-center">
        <div className="flex justify-center mb-6"><Logo /></div>
        <XCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-brand-navy mb-2">Paiement annulé</h1>
        <p className="text-sm text-slate-500 mb-6">Aucun montant n'a été débité. Votre campagne reste en brouillon.</p>
        <button
          data-testid="ad-payment-cancel-back"
          onClick={() => navigate("/publicite")}
          className="w-full bg-brand-navy text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-blue transition-colors"
        >
          Retour à mes campagnes
        </button>
      </div>
    </div>
  );
}
